import { __assign } from 'tslib';
import React, { useState, useRef, useCallback, useEffect, Fragment } from 'react';
import mergeRefs from 'react-merge-refs';
import cn from 'classnames';
import { Popover } from '@alfalab/core-components-popover/esm';

var defaultColors = {"hint":"tooltip__hint_1wh5o","arrow":"tooltip__arrow_1wh5o","tooltip":"tooltip__tooltip_1wh5o"};
require('./../default.css')

var styles = {"component":"tooltip__component_17d30","popper":"tooltip__popper_17d30","hint":"tooltip__hint_17d30","target":"tooltip__target_17d30","overlap":"tooltip__overlap_17d30","inline":"tooltip__inline_17d30"};
require('./../index.css')

var invertedColors = {"tooltip":"tooltip__tooltip_51t49","arrow":"tooltip__arrow_51t49","hint":"tooltip__hint_51t49"};
require('./../inverted.css')

var desktopStyles = {"popper":"tooltip__popper_vtgll","hint":"tooltip__hint_vtgll"};
require('./desktop.css')

var colorStyles = {
    default: defaultColors,
    inverted: invertedColors,
};
var DEFAULT_OFFSET = [0, 16];
var TooltipDesktop = function (_a) {
    var _b;
    var children = _a.children, content = _a.content, _c = _a.trigger, trigger = _c === void 0 ? 'hover' : _c, _d = _a.onCloseDelay, onCloseDelay = _d === void 0 ? 300 : _d, _e = _a.onOpenDelay, onOpenDelay = _e === void 0 ? 300 : _e, dataTestId = _a.dataTestId, forcedOpen = _a.open, _f = _a.offset, offset = _f === void 0 ? DEFAULT_OFFSET : _f, position = _a.position, contentClassName = _a.contentClassName, arrowClassName = _a.arrowClassName, popoverClassName = _a.popoverClassName, updatePopover = _a.updatePopover, targetClassName = _a.targetClassName, _g = _a.targetTag, TargetTag = _g === void 0 ? 'div' : _g, zIndex = _a.zIndex, onClose = _a.onClose, onOpen = _a.onOpen, getPortalContainer = _a.getPortalContainer, _h = _a.view, view = _h === void 0 ? 'tooltip' : _h, _j = _a.targetRef, targetRef = _j === void 0 ? null : _j, fallbackPlacements = _a.fallbackPlacements, _k = _a.preventOverflow, preventOverflow = _k === void 0 ? true : _k, _l = _a.availableHeight, availableHeight = _l === void 0 ? false : _l, _m = _a.anchor, anchor = _m === void 0 ? null : _m, _o = _a.colors, colors = _o === void 0 ? 'default' : _o, useAnchorWidth = _a.useAnchorWidth, onTargetClick = _a.onTargetClick, _p = _a.withTransition, withTransition = _p === void 0 ? true : _p;
    var _q = useState(!!forcedOpen), visible = _q[0], setVisible = _q[1];
    var _r = useState(null), target = _r[0], setTarget = _r[1];
    var contentRef = useRef(null);
    var timer = useRef(0);
    var show = forcedOpen === undefined ? visible : forcedOpen;
    var open = function () {
        if (!show) {
            setVisible(true);
            if (onOpen) {
                onOpen();
            }
        }
    };
    var close = useCallback(function () {
        if (show) {
            setVisible(false);
            if (onClose) {
                onClose();
            }
        }
    }, [onClose, show]);
    var toggle = function () {
        if (show) {
            close();
        }
        else {
            open();
        }
    };
    var clickedOutside = useCallback(function (node) {
        if (target && target.contains(node)) {
            return false;
        }
        if (contentRef.current && contentRef.current.contains(node)) {
            return false;
        }
        return true;
    }, [target]);
    useEffect(function () {
        var handleBodyClick = function (event) {
            var eventTarget = event.target;
            if (clickedOutside(eventTarget)) {
                close();
            }
        };
        document.body.addEventListener('click', handleBodyClick);
        document.body.addEventListener('touchstart', handleBodyClick);
        return function () {
            document.body.removeEventListener('click', handleBodyClick);
            document.body.removeEventListener('touchstart', handleBodyClick);
            clearTimeout(timer.current);
        };
    }, [clickedOutside, close]);
    var handleTargetClick = function (event) {
        event.persist();
        onTargetClick === null || onTargetClick === void 0 ? void 0 : onTargetClick(event);
        toggle();
    };
    var handleMouseOver = function () {
        clearTimeout(timer.current);
        timer.current = window.setTimeout(function () {
            open();
        }, onOpenDelay);
    };
    var handleMouseOut = function () {
        clearTimeout(timer.current);
        timer.current = window.setTimeout(function () {
            close();
        }, onCloseDelay);
    };
    var handleTouchStart = function (event) {
        var eventTarget = event.target;
        clearTimeout(timer.current);
        if (clickedOutside(eventTarget)) {
            timer.current = window.setTimeout(function () {
                close();
            }, onCloseDelay);
        }
        else {
            open();
        }
    };
    var onClickProps = { onClick: handleTargetClick };
    var onHoverProps = {
        onMouseOver: handleMouseOver,
        onMouseOut: handleMouseOut,
        onTouchStart: handleTouchStart,
    };
    var getTargetProps = function () {
        var _a;
        var props = {
            className: cn(styles.target, targetClassName, (_a = {},
                _a[styles.inline] = TargetTag === 'span',
                _a)),
        };
        switch (trigger) {
            case 'click':
                return __assign(__assign({}, props), onClickProps);
            case 'hover':
                return __assign(__assign({}, props), onHoverProps);
            default:
                return {};
        }
    };
    var getContentProps = function () {
        var props = {
            ref: contentRef,
            'data-test-id': dataTestId,
            className: cn(styles.component, contentClassName),
        };
        switch (trigger) {
            case 'hover':
                return __assign(__assign({}, props), onHoverProps);
            default:
                return props;
        }
    };
    return (React.createElement(Fragment, null,
        React.createElement(TargetTag, __assign({ ref: mergeRefs([targetRef, setTarget]) }, getTargetProps()),
            children.props.disabled && React.createElement("div", { className: styles.overlap }),
            children),
        React.createElement(Popover, { anchorElement: anchor || target, open: show, getPortalContainer: getPortalContainer, arrowClassName: cn(arrowClassName, styles.arrow, colorStyles[colors].arrow), popperClassName: cn(styles.popper, styles[view], colorStyles[colors][view], (_b = {},
                _b[desktopStyles.popper] = view === 'tooltip',
                _b[desktopStyles.hint] = view === 'hint',
                _b)), className: popoverClassName, offset: offset, withArrow: true, position: position, update: updatePopover, zIndex: zIndex, fallbackPlacements: fallbackPlacements, preventOverflow: preventOverflow, availableHeight: availableHeight, useAnchorWidth: useAnchorWidth, withTransition: withTransition },
            React.createElement("div", __assign({}, getContentProps()), content))));
};

export { TooltipDesktop };
